import { useEffect, useState } from "react";

type StepId = "connect" | "match" | "win";

type Step = {
  id: StepId;
  num: string;
  title: string;
  desc: string;
  metaLabel: string;
  metaValue: string;
  metaTone?: "brand" | "live" | "default";
};

const STEPS: Step[] = [
  {
    id: "connect",
    num: "01",
    title: "Connect",
    desc: "Web3 wallet or one-click email sign-in. Your USDT stays in self-custody until the moment you stake.",
    metaLabel: "CUSTODY",
    metaValue: "Self · non-custodial",
  },
  {
    id: "match",
    num: "02",
    title: "Match",
    desc: "Pick a game and stake amount. The matchmaker pairs you with a real opponent in under 2 seconds. Both wagers escrow on-chain.",
    metaLabel: "AVG MATCH TIME",
    metaValue: "1.6s",
    metaTone: "brand",
  },
  {
    id: "win",
    num: "03",
    title: "Win",
    desc: "Smart contract watches the game oracle and pays the winner instantly. No withdrawal queues, no review periods.",
    metaLabel: "AVG SETTLEMENT",
    metaValue: "~3s on-chain",
    metaTone: "live",
  },
];

function useTicker(intervalMs = 60) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((value) => value + 1), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);

  return tick;
}

function SectionGrid() {
  return (
    <div
      className="absolute inset-0 pointer-events-none opacity-35"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
        backgroundSize: "38px 38px",
        maskImage: "radial-gradient(circle at 50% 40%, #000 30%, transparent 84%)",
        WebkitMaskImage: "radial-gradient(circle at 50% 40%, #000 30%, transparent 84%)",
      }}
    />
  );
}

function ConnectIllustration() {
  const tick = useTicker();
  const t = tick / 14;
  const pulse = 0.45 + Math.abs(Math.sin(t)) * 0.55;
  const ring = (tick * 1.6) % 22;
  const dot = 124 + Math.sin(t * 1.1) * 28;

  return (
    <svg viewBox="0 0 220 140" className="w-full h-full" aria-hidden="true">
      <defs>
        <linearGradient id="connect-wallet-grad" x1="0" x2="1">
          <stop offset="0" stopColor="#ff9454" />
          <stop offset="1" stopColor="#ff6b00" />
        </linearGradient>
      </defs>

      <g transform="translate(38 36)">
        <rect x="0" y="0" width="86" height="60" rx="10" fill="#121212" stroke="url(#connect-wallet-grad)" strokeWidth="1.4" />
        <rect x="50" y="24" width="42" height="16" rx="4" fill="rgba(255,107,0,0.16)" />
        <circle cx="67" cy="32" r="3" fill="#ff6b00" />
        <rect x="10" y="11" width="44" height="4" rx="2" fill="#353535" />
        <rect x="10" y="20" width="30" height="3" rx="1.5" fill="#242424" />
      </g>

      <line x1="124" y1="66" x2="182" y2="66" stroke="#ff6b00" strokeWidth="1.5" strokeDasharray="4 4" opacity={pulse} />
      <circle cx={dot} cy="66" r="3.4" fill="#ff6b00" />

      <g transform="translate(176 48)">
        <rect x="0" y="0" width="30" height="30" rx="8" fill="#111111" stroke="#ff6b00" strokeWidth="1.2" />
        <path d="M8 8 L14 11 L14 20 L8 23 Z" stroke="#ff6b00" strokeWidth="1.4" fill="none" />
        <path d="M22 8 L16 11 L16 20 L22 23 Z" stroke="#ff6b00" strokeWidth="1.4" fill="none" />
      </g>

      <circle cx="81" cy="66" r={10 + ring} fill="none" stroke="#ff6b00" strokeWidth="1" opacity={Math.max(0, 0.5 - ring / 32)} />
    </svg>
  );
}

function MatchIllustration() {
  const tick = useTicker();
  const t = tick / 16;
  const left = 32 + Math.sin(t) * 5;
  const right = 188 + Math.cos(t) * 5;
  const meet = 0.45 + Math.sin(t * 0.8) * 0.2;

  return (
    <svg viewBox="0 0 220 140" className="w-full h-full" aria-hidden="true">
      <defs>
        <linearGradient id="match-left-grad" x1="0" x2="1">
          <stop offset="0" stopColor="#ff9454" />
          <stop offset="1" stopColor="#ff6b00" />
        </linearGradient>
      </defs>

      <g transform={`translate(${left} 70)`}>
        <circle r="15" fill="rgba(255,107,0,0.16)" />
        <circle r="10.5" fill="url(#match-left-grad)" />
      </g>

      <g transform={`translate(${right} 70)`}>
        <circle r="15" fill="rgba(255,255,255,0.08)" />
        <circle r="10.5" fill="#474747" />
      </g>

      <line x1={left + 11} y1="70" x2={right - 11} y2="70" stroke="#34D399" strokeWidth="1.2" strokeDasharray="2.5 3" opacity={0.55 + meet} />

      <g transform="translate(97 55)">
        <rect x="0" y="0" width="28" height="28" rx="7" fill="#0d1712" stroke="#34D399" strokeWidth="1.2" />
        <path d="M9 9 L19 19" stroke="#34D399" strokeWidth="1.4" strokeLinecap="round" />
        <path d="M19 9 L9 19" stroke="#34D399" strokeWidth="1.4" strokeLinecap="round" />
      </g>

      <circle
        cx="111"
        cy="69"
        r={8 + ((tick * 1.4) % 18)}
        fill="none"
        stroke="#34D399"
        strokeWidth="1"
        opacity={Math.max(0, 0.46 - ((tick * 1.4) % 18) / 40)}
      />

      <g transform={`translate(${left - 6} 97)`}>
        <rect x="0" y="0" width="16" height="11" rx="3" fill="#ff6b00" />
        <text x="8" y="8" textAnchor="middle" fontSize="7" fontWeight="700" fill="#0a0a0a">$</text>
      </g>
      <g transform={`translate(${right - 6} 97)`}>
        <rect x="0" y="0" width="16" height="11" rx="3" fill="#ff6b00" />
        <text x="8" y="8" textAnchor="middle" fontSize="7" fontWeight="700" fill="#0a0a0a">$</text>
      </g>
    </svg>
  );
}

function WinIllustration() {
  const tick = useTicker();
  const t = tick / 18;
  const coins = Array.from({ length: 7 }, (_, index) => {
    const phase = (t * 0.6 + index * 0.18) % 1;
    const opacity = Math.max(0, Math.sin(phase * Math.PI));
    const x = 110 + Math.sin(index * 1.7) * 35;
    const y = 22 + phase * 72;

    return (
      <g key={index} transform={`translate(${x} ${y})`} opacity={opacity}>
        <circle r="6" fill="#ff6b00" />
        <text x="0" y="3" textAnchor="middle" fontSize="8" fontWeight="700" fill="#0a0a0a">$</text>
      </g>
    );
  });

  return (
    <svg viewBox="0 0 220 140" className="w-full h-full" aria-hidden="true">
      {coins}

      <g transform="translate(80 96)">
        <rect x="0" y="0" width="60" height="34" rx="7" fill="#121212" stroke="#34D399" strokeWidth="1.3" />
        <rect x="33" y="12" width="27" height="10" rx="3" fill="rgba(52,211,153,0.18)" />
        <circle cx="44" cy="17" r="2.4" fill="#34D399" />
        <text x="18" y="22" textAnchor="middle" fontSize="11" fontWeight="700" fill="#34D399">+$</text>
      </g>

      {[0, 1, 2, 3].map((index) => {
        const y = 48 + Math.sin(t * 2 + index) * 6;
        const x = 46 + index * 42;
        return (
          <g key={index} transform={`translate(${x} ${y})`}>
            <path d="M0 -5 L1.5 -1.5 L5 0 L1.5 1.5 L0 5 L-1.5 1.5 L-5 0 L-1.5 -1.5 Z" fill="#34D399" opacity="0.65" />
          </g>
        );
      })}
    </svg>
  );
}

function SecurityShield() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" stroke="var(--color-live)" strokeWidth="1.6" />
      <path d="M9 12l2 2 4-4" stroke="var(--color-live)" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function StepCard({
  step,
  active,
  onActivate,
}: {
  step: Step;
  active: boolean;
  onActivate: () => void;
}) {
  const Art = step.id === "connect" ? ConnectIllustration : step.id === "match" ? MatchIllustration : WinIllustration;
  const metaColor =
    step.metaTone === "live"
      ? "var(--color-live)"
      : step.metaTone === "brand"
        ? "var(--color-brand)"
        : "var(--color-text-primary)";

  return (
    <article
      className="relative rounded-[18px] p-5 md:p-6 transition-all duration-300 cursor-default overflow-hidden"
      style={{
        background: active
          ? "linear-gradient(180deg, rgba(24,24,24,0.98), rgba(14,14,14,1))"
          : "linear-gradient(180deg, rgba(19,19,19,0.94), rgba(12,12,12,0.98))",
        border: active ? "1px solid var(--color-brand-line)" : "1px solid var(--color-line)",
        boxShadow: active
          ? "0 24px 60px rgba(0,0,0,0.38), 0 0 0 1px rgba(255,107,0,0.06) inset"
          : "0 18px 40px rgba(0,0,0,0.22)",
        transform: active ? "translateY(-4px)" : "translateY(0)",
      }}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      tabIndex={0}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: active
            ? "radial-gradient(circle at 76% 16%, rgba(255,107,0,0.12), transparent 30%), radial-gradient(circle at 26% 72%, rgba(52,211,153,0.09), transparent 30%)"
            : "radial-gradient(circle at 76% 16%, rgba(255,107,0,0.06), transparent 26%)",
        }}
      />

      <div className="relative flex items-center justify-between gap-3">
        <span className="font-mono text-[11px] tracking-[0.18em]" style={{ color: active ? "var(--color-brand)" : "var(--color-text-tertiary)" }}>
          {step.num}
        </span>
        <div className="flex-1 h-px relative overflow-hidden rounded-full bg-[var(--color-line)]">
          <div
            className="absolute left-0 top-0 bottom-0 rounded-full transition-all duration-700"
            style={{
              width: active ? "100%" : "28%",
              background: active ? "var(--color-brand)" : "var(--color-text-tertiary)",
              boxShadow: active ? "0 0 10px rgba(255,107,0,0.5)" : "none",
            }}
          />
        </div>
        <span
          className="w-2 h-2 rounded-full transition-all duration-300"
          style={{
            background: active ? "var(--color-brand)" : "var(--color-text-tertiary)",
            boxShadow: active ? "0 0 10px rgba(255,107,0,0.45)" : "none",
          }}
        />
      </div>

      <div
        className="relative mt-5 h-[140px] rounded-[14px] overflow-hidden"
        style={{
          background: "radial-gradient(circle at 50% 50%, rgba(255,107,0,0.13), rgba(255,107,0,0.05) 28%, rgba(13,13,13,1) 76%)",
          border: "1px solid var(--color-line)",
        }}
      >
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,107,0,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,0,0.04) 1px, transparent 1px)",
            backgroundSize: "34px 34px",
            maskImage: "radial-gradient(circle at center, #000 36%, transparent 88%)",
            WebkitMaskImage: "radial-gradient(circle at center, #000 36%, transparent 88%)",
          }}
        />
        <div className="relative w-full h-full p-3">
          <Art />
        </div>
      </div>

      <h3 className="relative mt-5 text-[24px] font-display font-bold tracking-[-0.03em] text-[var(--color-text-primary)]">
        {step.title}
      </h3>
      <p className="relative mt-2 text-[14px] leading-[1.65] text-[var(--color-text-secondary)]">
        {step.desc}
      </p>

      <div className="relative mt-5 pt-4 flex items-center justify-between gap-4" style={{ borderTop: "1px dashed var(--color-line)" }}>
        <span className="font-mono text-[10px] tracking-[0.16em] text-[var(--color-text-tertiary)]">{step.metaLabel}</span>
        <span className="font-mono text-[11px] font-semibold tracking-[0.06em]" style={{ color: metaColor }}>
          {step.metaValue}
        </span>
      </div>
    </article>
  );
}

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setActiveStep((value) => (value + 1) % STEPS.length);
    }, 2800);
    return () => window.clearInterval(id);
  }, []);

  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28">
        <div
          className="absolute inset-x-0 top-8 bottom-8 rounded-[32px] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 720px 420px at 82% 18%, rgba(255,107,0,0.06), transparent 62%), radial-gradient(ellipse 540px 300px at 18% 76%, rgba(52,211,153,0.05), transparent 64%)",
          }}
        />
        <SectionGrid />

        <div className="relative">
          <div className="max-w-[760px]">
            <div className="font-mono text-[11px] tracking-[0.18em] font-semibold flex items-center gap-2 text-[var(--color-brand)]">
              <span>◆</span>
              HOW IT WORKS
            </div>
            <h2 className="mt-3 text-[38px] md:text-[52px] lg:text-[58px] leading-[0.98] font-display font-extrabold tracking-[-0.05em] text-[var(--color-text-primary)]">
              From wallet to{" "}
              <span
                style={{
                  background: "linear-gradient(180deg, #ff9b62, #ff6b00 58%, #d9571f)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                win
              </span>{" "}
              in three steps.
            </h2>
            <p className="mt-4 max-w-[600px] text-[15px] md:text-[16px] leading-[1.7] text-[var(--color-text-secondary)]">
              The entire flow is handled by an audited escrow contract on Solana.
              <span className="text-[var(--color-text-tertiary)]"> No backend tricks, no withdrawal queues, no house edge.</span>
            </p>
          </div>
        </div>

        <div className="relative mt-10 grid grid-cols-1 xl:grid-cols-3 gap-4">
          {STEPS.map((step, index) => (
            <StepCard key={step.id} step={step} active={activeStep === index} onActivate={() => setActiveStep(index)} />
          ))}
        </div>

        <div
          className="relative mt-6 rounded-[18px] px-5 py-5 md:px-6 md:py-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
          style={{
            background: "linear-gradient(180deg, rgba(17,17,17,0.98), rgba(12,12,12,1))",
            border: "1px solid var(--color-line)",
            boxShadow: "0 18px 40px rgba(0,0,0,0.22)",
          }}
        >
          <div className="flex items-start gap-4">
            <div
              className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0"
              style={{
                background: "rgba(52,211,153,0.08)",
                border: "1px solid rgba(52,211,153,0.28)",
              }}
            >
              <SecurityShield />
            </div>
            <div>
              <div className="text-[15px] font-semibold tracking-[-0.02em] text-[var(--color-text-primary)]">
                Every match settled by the same audited contract.
              </div>
              <div className="mt-1 font-mono text-[10px] md:text-[11px] tracking-[0.05em] text-[var(--color-text-tertiary)]">
                RAW7dgEuw7NUs8fX…UwYw · 4,238 matches in the last 24h · 0 disputes
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              className="font-mono rounded-lg px-4 py-2.5 text-[11px] font-semibold tracking-[0.12em] transition-colors"
              style={{
                background: "transparent",
                border: "1px solid var(--color-line-emphasis)",
                color: "var(--color-text-secondary)",
              }}
            >
              READ AUDIT →
            </button>
            <button
              type="button"
              className="font-mono rounded-lg px-4 py-2.5 text-[11px] font-bold tracking-[0.12em] transition-transform hover:scale-[1.01]"
              style={{
                background: "var(--color-brand)",
                border: "1px solid var(--color-brand)",
                color: "#0a0a0a",
                boxShadow: "0 8px 20px rgba(255,107,0,0.22)",
              }}
            >
              VIEW ON SOLSCAN →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
